import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { DwOrmDataServiceToken, DwOrmDataService, DwMetaDataServiceToken, DwMetaDataService, DwSecurityUserService, DwCacheService, DwEventService, DwConfigService, DwConfigServiceToken, DwQueryHelper, DwSecurityTopics, SuppressErrorMessagesHeader } from "@devwareapps/devware-cap";
import { concat, EMPTY, Observable, of, ReplaySubject } from "rxjs";
import { QuizEntity, AppMetaDataItemNames, FlightSchoolGroupEntity, FlightSchoolGroupWithStudentCourseDetailQueryEntity, AppMetaData, StudentCourseEntity, PackageEntity, RoUserRoleDetailEntity, JoinRequestEntity, JoinRequestEmailEntity, FlightSchoolEntity, StandardColorSetEntity, ReferenceStatusAllItems, StudentEntity, CoursePurchaseEntity, StudentBySchoolQueryEntity, CourseEntity, DwRoMediaEntity, StandardLoginMediaWithMediaPathQueryEntity, SubscriptionFeatureKeys, FlightSchoolReportsCourseCountsQueryEntity } from "../../../meta-data/app-meta-data.service";
import { catchError, delay, distinctUntilChanged, map, mergeMap, tap } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ResetStudentAssignmentResult } from "../models/reset-student-assignment-result.model";
import { ResetStudentAssignmentRequest } from "../models/reset-student-assignment-request.model";
import { StudentCourseProgressResult } from "../models/student-course-progress-result.model";
import { StudentCourseProgressRequest } from "../models/student-course-progress-request.model";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";
import { StudentChangeSchoolRequest } from "../models/student-change-school-request.model";
import { StudentChangeSchoolResult } from "../models/student-change-school-result.model";
import { SchoolFeatures } from "../models/school-features.model";
import { SchoolCourseEndorsementActionRequest } from "../models/school-course-endorsement-action-request.model";
import { SchoolCourseEndorsementActionResult } from "../models/school-course-endorsement-action-result.model";

class SchoolFeaturesMismatchError extends Error { }

@Injectable({
    providedIn: 'root'
})
export class SchoolRepositoryService {
    studentProgressApi: string;
    schoolAdminApi: string;
    schoolApi: string;
    private readonly schoolFeaturesSubject = new ReplaySubject<SchoolFeatures>(1);

    constructor(@Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) configService: DwConfigService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwCacheService: DwCacheService,
        private dwEventService: DwEventService,
        private router: Router,
        private http: HttpClient,
        private dateTimeUtilServie: DateTimeUtilService,
    ) {
        this.studentProgressApi = `${configService.coreConfig.apiRoot}/aviator-online/students/progress`;
        this.schoolAdminApi = `${configService.coreConfig.apiRoot}/aviator-online/school/admin`;
        this.schoolApi = `${configService.coreConfig.apiRoot}/aviator-online/school`;
        this.schoolAdminApi = `${configService.coreConfig.apiRoot}/aviator-online/school/admin`;
    }

    getFlightSchool(flightSchoolId: any): Observable<FlightSchoolEntity> {
        const query = AppMetaData.FlightSchool.CreateQueryBuilder();

        query.query.FieldSettings.LoadAllLookupDisplayFields = false;

        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, flightSchoolId));

        query.addPrefetch(r => r.FlightSchoolBranding);

        return this.dwOrmDataService.executeQuerySingle(query.query);
    }

    getStudentsFromUserIds(userIds: number[]): Observable<StudentEntity[]> {
        const query = AppMetaData.Student.CreateQueryBuilder();
  
        query.addFilterAnd(f => f.InList(a => a.UserId, userIds));
  
        return this.dwOrmDataService.executeQuery(query.query)
      }

    getCurrentSchoolId(): Observable<number> {
        const query = AppMetaData.UserDetail.CreateQueryBuilder();

        const userId = this.dwSecurityUserService.securityState.securityContext?.ApplicationUser?.UserId;

        if (!userId) {
            return of(null);
        }

        query.addFilterAnd(f => f.Equal(a => a.UserId, userId));

        return this.dwOrmDataService.executeQuerySingle(query.query)
            .pipe(map((schoolUser) => {
                return schoolUser?.FlightSchoolId
            }));
        ;
    }

    resendJoinRequest(joinRequestId: number): Observable<any> {
        if (!joinRequestId) {
            return of(null);
        }

        return this.loadJoinRequest(joinRequestId)
            .pipe(mergeMap(joinRequest => {
                return this.resendJoinRequestEmails(joinRequest.JoinRequestEmail)
            }));
    }

    resendJoinRequestEmails(emails: JoinRequestEmailEntity[]): Observable<any> {
        const emailsToSave: JoinRequestEmailEntity[] = [];
        for (let email of emails) {
            if (!email.Accepted)
                email.LastResendRequestDateTime = this.dateTimeUtilServie.getCurrentDateTime();

            emailsToSave.push(email);
        }

        if (emailsToSave.length==0) {
            return of();
        }

        return this.dwOrmDataService.saveEntityCollection(emailsToSave);
    }


    loadJoinRequest(joinRequestId: number): Observable<JoinRequestEntity> {
        const query = AppMetaData.JoinRequest.CreateQueryBuilder();

        query.query.FieldSettings.LoadAllLookupDisplayFields = false;

        query.addFilterAnd(filters => filters.Equal(f => f.JoinRequestId, joinRequestId));

        query.addPrefetch(r => r.JoinRequestEmail);

        return this.dwOrmDataService.executeQuerySingle(query.query);
    }

    getUserRoleDetaill(userId: number): Observable<RoUserRoleDetailEntity> {
        const query = AppMetaData.RoUserRoleDetail.CreateQueryBuilder();

        query.query.FieldSettings.LoadAllLookupDisplayFields = false;

        query.addFilterAnd(filters => filters.Equal(f => f.UserId, userId || -1));

        return this.dwOrmDataService.executeQuerySingle(query.query);
    }

    getStudentCourses(studentId: number): Observable<StudentCourseEntity[]> {
        const query = AppMetaData.StudentCourse.CreateQueryBuilder();


        query.addFilterAnd(f => f.Equal(a => a.StudentId, studentId));

        return this.dwOrmDataService.executeQuery(query.query);
    }

    getCoursePackages(): Observable<PackageEntity[]> {
        const query = AppMetaData.Package.CreateQueryBuilder();

        query.addPrefetch(r => r.PackageCourse);

        return this.dwOrmDataService.executeQuery(query.query);
    }

    assignCourses(studentId: number, courseIds: number[]): Observable<StudentCourseEntity[]> {
        const studentCourses: StudentCourseEntity[] = [];

        return this.getStudentCourses(studentId)
            .pipe(mergeMap(existingCourses => {

                for (const courseId of courseIds) {

                    const currentExistingCourses = existingCourses.filter(c => c.CourseId == courseId);

                    if (currentExistingCourses.length > 0) {
                        const existingActiveCourse = currentExistingCourses.find(c => c.StudentCourseStatusId == ReferenceStatusAllItems.Active);

                        if (existingActiveCourse) {
                            continue;
                        }

                        // Otherwise, they have an inactive course, so we need to reactivate it

                        const existingInactiveCourse = currentExistingCourses[0];

                        existingInactiveCourse.StudentCourseStatusId = ReferenceStatusAllItems.Active;
                        studentCourses.push(existingInactiveCourse);
                        continue;
                    }

                    const newStudentCourse: StudentCourseEntity = {
                        _itemName: AppMetaDataItemNames.StudentCourse,
                        StudentId: studentId,
                        CourseId: courseId,
                    }

                    studentCourses.push(newStudentCourse);
                }

                if (studentCourses.length == 0) {
                    return of(studentCourses);
                }

                return this.dwOrmDataService.saveEntityCollection(studentCourses, false, false)
                    .pipe(tap(results => {
                        // We need to trigger that student was updated so related grids are updated (Course progress grid is based on student and not student course so won't update normally)
                        this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Student.ItemDetail.itemName), {});
                    }));
            }));
    }

    /*
        Methods for Purchase Course Util


    */

    getStudentsWithCourseDetails(studentIds : number[]): Observable<StudentBySchoolQueryEntity[]> {
        if (!studentIds || studentIds.length == 0) {
            return of([])
        }

        return AppMetaData.Queries.StudentBySchool.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(query => {
                const qh = new DwQueryHelper();

                // clone query so we don't modify the original (//TODO: Need to fix this in the framework - thought it was resolved, but it came back here)
                query.query = qh.cloneQuery(query.query);

                query.addFilterAnd(f => f.InList(a => a.StudentId, studentIds));

                query.addPrefetch(r => r.StudentCourse);

                return this.dwOrmDataService.executeQuery(query.query);
            }));
    }

    getGroupStudentsWithCourseDetails(flightSchoolGroupId: number): Observable<StudentBySchoolQueryEntity[]> {
        const query = AppMetaData.FlightSchoolGroupStudent.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolGroupId, flightSchoolGroupId));

        return this.dwOrmDataService.executeQuery(query.query)
            .pipe(mergeMap(groupStudents => {
                const studentIds = groupStudents.map(s => s.StudentId);

                if (studentIds.length == 0) {
                    return of([]);
                }

                return this.getStudentsWithCourseDetails(studentIds);
            }));
    }

    // getCourseForPackages(packageIds: number[]): Observable<PackageEntity[]> {
    //     const query = AppMetaData.PackageCourse.CreateQueryBuilder();

    //     query.addFilterAnd(f => f.InList(a => a.PackageId, packageIds));
    //     query.addPrefetch(c => c.Course);

    //     return this.dwOrmDataService.executeQuery(query.query)
    // }


    getAllCoursesWithPackageDetail(): Observable<CourseEntity[]> {
        const cacheInvalidationTopics: string[] = [];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Course.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.PackageCourse.ItemDetail.itemName));

        return this.dwCacheService.get('allCoursesWithPackageDetail', this.loadAllCoursesWithPackageDetail(), null, cacheInvalidationTopics);
    }

    private loadAllCoursesWithPackageDetail(): Observable<CourseEntity[]> {
        const query = AppMetaData.Course.CreateQueryBuilder();

        query.addPrefetch(c => c.PackageCourse);
    
        return this.dwOrmDataService.executeQuery(query.query);
    }


    /** Gets a stuent group with course details */
    getStudentGroup(flightSchoolGroupId: number, courseId: number, flightSchoolId: number): Observable<FlightSchoolGroupWithStudentCourseDetailQueryEntity[]> {

        return AppMetaData.Queries.FlightSchoolGroupWithStudentCourseDetail.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(query => {

                query.addFilterAnd(f => f.Equal(a => a.FlightSchoolGroupId, flightSchoolGroupId));
                query.addFilterAnd(f => f.Equal(a => a.StudentCourseCourseId, courseId));
                query.addFilterAnd(f => f.Equal(a => a.StudentCourseIsActive, true));

                // Map 2 fligh school ids (one for the group and one for the student)
                query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, flightSchoolId));

                return this.dwOrmDataService.executeQuery(query.query);
            }));
    }

    resetStudentAssignmentProgress(request: ResetStudentAssignmentRequest): Observable<ResetStudentAssignmentResult> {
        const url = `${this.studentProgressApi}/reset-student-assignment-progress`;

        return this.http.post<ResetStudentAssignmentResult>(url, request);
    }

     studentCourseEndorsementAction(request: SchoolCourseEndorsementActionRequest): Observable<SchoolCourseEndorsementActionResult> {
        const url = `${this.studentProgressApi}/student-course-endorsement-action`;

        return this.http.post<SchoolCourseEndorsementActionResult>(url, request)
            .pipe(tap(result => {
                if (result.Success) {
                    // We need to trigger that student was updated so related grids are updated (Course progress grid is based on student and not student course)
                    this.publichStudentProgressChangedEvents();
                }
            }));
    }

    studentChangeSchool(request: StudentChangeSchoolRequest): Observable<StudentChangeSchoolResult> {
        const url = `${this.schoolAdminApi}/student-change-school`;

        return this.http.post<StudentChangeSchoolResult>(url, request)
            .pipe(tap(result => {

                if (result.IsSuccess){
                     this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.DwUser.ItemDetail.itemName), {});
                     this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.UserDetail.ItemDetail.itemName), {});
                     this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Student.ItemDetail.itemName), {});
                     this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Assignment.ItemDetail.itemName), {});
                }
            }))
    }

    hasFeatureWithLoad(featureKey: SubscriptionFeatureKeys): Observable<boolean> {
        return this.getSchoolFeatures()
            .pipe(map(schoolFeatures => {

                return this.hasFeature(schoolFeatures, featureKey);
            }));
    }

    hasAnyFeatureWithLoad(...featureKeys: SubscriptionFeatureKeys[]): Observable<boolean> {
        return this.getSchoolFeatures()
            .pipe(map(schoolFeatures => {
                if (schoolFeatures == null) {
                    return false;
                }
                return !!schoolFeatures.Features.find(featureId =>  featureKeys.indexOf(featureId) >=0);
            }));
    }

    hasFeature(schoolFeatures: SchoolFeatures, featureKey: SubscriptionFeatureKeys): boolean {
        if (schoolFeatures == null) {
            return false;
        }
        return !!schoolFeatures.Features.find(featureId =>  featureId == featureKey);
    }

    hasAnyFeature(schoolFeatures: SchoolFeatures, ...featureKeys: SubscriptionFeatureKeys[]): boolean {
        if (schoolFeatures == null) {
            return false;
        }
        return !!schoolFeatures.Features.find(featureId =>  featureKeys.indexOf(featureId) >=0);
    }

    public REFRESH_SCHOOL_FEAUTURES_EVENT = 'REFRESH_SCHOOL_FEAUTRES_EVENT';

    refreshSchoolFeatures():Observable<SchoolFeatures> {
        this.dwEventService.publishEvent(this.REFRESH_SCHOOL_FEAUTURES_EVENT, {});

        return of(null)
            .pipe(
                delay(100),
                mergeMap(() => {
                    return this.getSchoolFeatures();
                })
        );
    }

    getSchoolFeatures(): Observable<SchoolFeatures> {
        const cacheInvalidationTopics: string[] = [];

        cacheInvalidationTopics.push(DwSecurityTopics.SECURITY_CHANGED);
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Course.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchool.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Subscription.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.SubscriptionTier.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.SubscriptionTierFeature.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.SubscriptionTierRole.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.PaymentMethod.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchoolRegistrationLink.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchoolRegistrationEmailDomain.ItemDetail.itemName));
        cacheInvalidationTopics.push(this.REFRESH_SCHOOL_FEAUTURES_EVENT);

        const cacheKey = this.getSchoolFeaturesCacheKey();

        return this.dwCacheService.get(cacheKey, this.loadSchoolFeatures(), null, cacheInvalidationTopics)
            .pipe(catchError(error => {
                if (error instanceof SchoolFeaturesMismatchError) {
                    return EMPTY;
                }

                const features : Partial<SchoolFeatures> = {
                    Features: [],
                    SubscriptionTierRoleIds: [],
                    FlightSchoolId: null,
                    FlightSchoolName: null,
                    FlightSchoolUrl: null,
                    FlightSchoolRegistrationUrl: null,
                    FlightSchoolInstructorRegistrationUrl: null,
                    RegistrationTypeKey: null,
                    AllowInstructorRegistrationRoleSelection: false,
                    RestrictRegistrationEmailDomains: false,
                    AllowedRegistrationEmailDomains: [],
                    IsMarketplaceEnabled: false,
                    SubscriptionTierId: null,
                    SubscriptionTierName: null,
                    CourseCount:1,
                    PackageCount:1,
                    HasDefaultPaymentMethod: null,
                    UniversityBillingModeEnabled: false,
                }

                return of(features as SchoolFeatures);
            }), tap(features => this.schoolFeaturesSubject.next(features)));
    }

    watchSchoolFeatures(): Observable<SchoolFeatures> {
        return concat(this.getSchoolFeatures(), this.schoolFeaturesSubject.asObservable())
            .pipe(distinctUntilChanged((previous, current) => this.schoolFeaturesAreEqual(previous, current)));
    }

    getSchoolFeaturesForSchool(flightSchoolId: number): Observable<SchoolFeatures> {
        const url = `${this.schoolApi}/get-school-features-any?flightSchoolId=${flightSchoolId}`;

        return this.http.get<SchoolFeatures>(url);
    }
    
    private loadSchoolFeatures(): Observable<SchoolFeatures> {
        const url = `${this.schoolApi}/get-school-features`;

        let headers = new HttpHeaders();
        
        headers = headers.append(SuppressErrorMessagesHeader, 'true');
        
        return this.http.get<SchoolFeatures>(url, { headers: headers })
            .pipe(map(schoolFeatures => {
                // Evaluate the expected school when the response arrives. This also
                // prevents a slower anonymous startup request from winning after login.
                const expectedFlightSchoolId = this.getAuthenticatedFlightSchoolId();
                if (expectedFlightSchoolId && schoolFeatures?.FlightSchoolId !== expectedFlightSchoolId) {
                    throw new SchoolFeaturesMismatchError(
                        `School features returned for school ${schoolFeatures?.FlightSchoolId}; expected ${expectedFlightSchoolId}.`);
                }

                // Move to server side to add school signup logic
                //schoolFeatures.FlightSchoolRegistrationUrl = `${schoolFeatures.FlightSchoolUrl}/register`;

                return schoolFeatures;
            }));
    }

    private getSchoolFeaturesCacheKey(): string {
        const securityState = this.dwSecurityUserService.securityState;
        if (!securityState?.isAuthenticated) {
            return 'schoolFeatures:anonymous';
        }

        const flightSchoolId = this.getAuthenticatedFlightSchoolId() || 'current';
        return `schoolFeatures:authenticated:${flightSchoolId}`;
    }

    private getAuthenticatedFlightSchoolId(): number {
        const securityState = this.dwSecurityUserService.securityState;
        if (!securityState?.isAuthenticated) {
            return null;
        }

        return (securityState.securityContext?.ApplicationUser as any)?.FlightSchoolId || null;
    }

    private schoolFeaturesAreEqual(previous: SchoolFeatures, current: SchoolFeatures): boolean {
        return JSON.stringify(previous) === JSON.stringify(current);
    }
    
    resetStudentCourseProgress(request: StudentCourseProgressRequest): Observable<StudentCourseProgressResult> {
        const url = `${this.studentProgressApi}/reset-student-course-progress`;

        return this.http.post<StudentCourseProgressResult>(url, request).pipe(map(result => {
            if (result.IsSuccess) {
                // We need to trigger that student was updated so related grids are updated (Course progress grid is based on student and not student course)
                this.publichStudentProgressChangedEvents();
            }

            return result;
        }))
    }

    updateStudentCourseProgress(request: StudentCourseProgressRequest): Observable<StudentCourseProgressResult> {
        const url = `${this.studentProgressApi}/update-student-course-progress`;

        return this.http.post<StudentCourseProgressResult>(url, request).pipe(map(result => {
            if (result.IsSuccess) {
                // We need to trigger that student was updated so related grids are updated (Course progress grid is based on student and not student course)
                this.publichStudentProgressChangedEvents();
            }

            return result;
        }))
    }

    private publichStudentProgressChangedEvents() {
        // We need to trigger that student was updated so related grids are updated (Course progress grid is based on student and not student course)
        this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Student.ItemDetail.itemName), {});
        this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.StudentCourse.ItemDetail.itemName), {});
        this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Course.ItemDetail.itemName), {});
    }


    updateCurrentStudentCourseProgress(courseId: number): Observable<StudentCourseProgressResult> {
        const url = `${this.studentProgressApi}/update-current-student-course-progress?courseId=${courseId}`;

        return this.http.post<StudentCourseProgressResult>(url, {});
    }

    updateAllCourseProgress(courseId: number): Observable<StudentCourseProgressResult> {
        const url = `${this.studentProgressApi}/update-all-course-progress?courseId=${courseId}`;

        return this.http.post<StudentCourseProgressResult>(url, {});
    }

    deleteStudentCourse(request: StudentCourseProgressRequest): Observable<StudentCourseProgressResult> {
        const url = `${this.studentProgressApi}/delete-student-course`;

        return this.http.post<StudentCourseProgressResult>(url, request)
            .pipe(map(result => {
                if (result.IsSuccess) {
                    // We need to trigger that student was updated so related grids are updated (Course progress grid is based on student and not student course)
                    this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Student.ItemDetail.itemName), {});
                }

                return result;
            }))
    }

    getSchoolCourseCounts(FlightSchoolId: number): Observable<FlightSchoolReportsCourseCountsQueryEntity[]>  {
      return AppMetaData.Queries.FlightSchoolReportsCourseCounts.CreateQueryBuilder(this.dwMetaDataService)
        .pipe(mergeMap(query => {

            query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, FlightSchoolId));

            return this.dwOrmDataService.executeQuery(query.query);
        }));
    }

    getColorSets() : Observable<StandardColorSetEntity[]> {
        const cacheInvalidationTopics: string[] = [];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.StandardColorSet.ItemDetail.itemName));

        return this.dwCacheService.get('colorSets', this.loadColorSets(), null, cacheInvalidationTopics);
    }

    validateRegistrationEmail(email: string): Observable<void> {
        const url = `${this.schoolApi}/validate-registration-email`;
        let headers = new HttpHeaders();

        headers = headers.append(SuppressErrorMessagesHeader, 'true');

        return this.http.post<void>(url, { Email: email }, { headers });
    }

    getStandardLoginMediaOptions(): Observable<StandardLoginMediaWithMediaPathQueryEntity[]> {
        const cacheInvalidationTopics: string[] = [];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.StandardLoginMedia.ItemDetail.itemName));

        return this.dwCacheService.get('standardLoginMediaOptions', this.loadStandardLoginMediaOptions(), null, cacheInvalidationTopics);
    }

    getCustomLoginMediaOptions(mediaGroupId: number): Observable<DwRoMediaEntity[]> {
        if (!mediaGroupId) {
            return of([]);
        }

        const cacheInvalidationTopics: string[] = [];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.DwRoMedia.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.DwMediaGroup.ItemDetail.itemName));

        return this.dwCacheService.get(`customLoginMediaOptions-${mediaGroupId}`, this.loadCustomLoginMediaOptions(mediaGroupId), null, cacheInvalidationTopics);
    }

    private loadColorSets() {
        const query = AppMetaData.StandardColorSet.CreateQueryBuilder();
    
        return this.dwOrmDataService.executeQuery(query.query);
    }

    private loadStandardLoginMediaOptions(): Observable<StandardLoginMediaWithMediaPathQueryEntity[]> {
        return AppMetaData.Queries.StandardLoginMediaWithMediaPath.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(query => this.dwOrmDataService.executeQuery(query.query)));
    }

    private loadCustomLoginMediaOptions(mediaGroupId: number): Observable<DwRoMediaEntity[]> {
        const query = AppMetaData.DwRoMedia.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.MediaGroupId, mediaGroupId));

        return this.dwOrmDataService.executeQuery(query.query);
    }
}
